import {
  AloftPrice,
  CraftsmanAdjustmentProps,
  ORDER_FEATURES,
  SalesComparisonApproachType,
  applyRoundingAndThresholds,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
  schPositiveInt,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectCompTypes,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateFireplaceAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
) => {
  // No unit-rate adjustments for fireplace
  if (salesComparisonApproach === 'unit_rate') return undefined;

  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;
  if (compType === 'arv') subjectImprovements = improvements.subjectArv;

  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;
  const compPropertyIdentification = propertyIdentification[compID];

  if (
    !subjectImprovements ||
    !compImprovements ||
    !depreciationRatios ||
    !subjectPropertyIdentification ||
    !compPropertyIdentification
  ) {
    return undefined;
  }

  const depreciationRatio = depreciationRatios.subject;
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) return undefined;

  const { fireplace } = subjectImprovements;
  const { fireplace: compFireplace } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;
  const {
    address: { PostalCode: compZipCode },
  } = compPropertyIdentification;

  if (isUndefined(zipCode) || isUndefined(compZipCode)) return undefined;

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  const absence = {
    count: schPositiveInt.parse(0),
    type: 'freestanding' as const,
    zipCode,
  };

  return adjCalculator.getFireplaceAdj({
    subject:
      isDefined(fireplace) &&
      fireplace.type &&
      fireplace.count &&
      ORDER_FEATURES.fireplace.hasFeature(subjectImprovements)
        ? {
            count: fireplace?.count,
            type: fireplace?.type,
            zipCode,
          }
        : absence,
    comp:
      isDefined(compFireplace) &&
      compFireplace.type &&
      compFireplace.count &&
      ORDER_FEATURES.fireplace.hasFeature(compImprovements)
        ? {
            count: compFireplace.count,
            type: compFireplace.type,
            zipCode: compZipCode,
          }
        : absence,
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;
};

const selectFireplaceAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'fireplace'),
    (state, id: string) => id,
    selectCompTypes,
    selectSalesComparisonApproach,
  ],
  calculateFireplaceAdj,
);

export const selectFireplaceAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'fireplace'),
    selectCompSequence,
    selectCompTypes,
    selectSalesComparisonApproach,
  ],
  (
    adjustmentVersion,
    improvements,
    propertyIdentification,
    depreciationRatios,
    compSequence,
    compTypes,
    salesComparisonApproach,
  ) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateFireplaceAdj(
        adjustmentVersion,
        improvements,
        propertyIdentification,
        depreciationRatios,
        propID,
        compTypes,
        salesComparisonApproach,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedFireplaceAdj = createPriceSelector(
  [selectFireplaceAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'fireplace', adjSettings),
);

export const selectAppliedFireplaceAdjDisplay = createSelector(
  [selectAppliedFireplaceAdj],
  (adj) => {
    if (!adj) return undefined;

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);

export const selectAppliedFireplaceAdjsDisplay = createSelector(
  [selectFireplaceAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (fireplaceAdjs, adjSettings, manualAdjustmentsEntities) =>
    Object.keys(fireplaceAdjs).reduce(
      (acc: Record<string, { value: string; color: string }>, propID) => {
        const adj = fireplaceAdjs[propID];
        const manualAdjustment = manualAdjustmentsEntities[propID]?.fireplace;

        if (manualAdjustment) {
          const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
          acc[propID] = {
            value: getStringValueFromAdjustment(manualAdjustmentPrice),
            color: getColorFromAdjustment(manualAdjustmentPrice),
          };
        } else if (adj) {
          acc[propID] = {
            value: getStringValueFromAdjustment(
              applyRoundingAndThresholds(fireplaceAdjs[propID], 'fireplace', adjSettings),
            ),
            color: getColorFromAdjustment(fireplaceAdjs[propID]),
          };
        }

        return acc;
      },
      {},
    ),
);
